'use no memo'; // React 19 breaks column resizing https://github.com/TanStack/table/issues/5567
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { first } from 'lodash';
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  Row,
  TableOptions,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';

import { useStickyElement } from '@/lib/hooks/use-sticky-element';
import { unlessNil } from '@/lib/utils';
import { NoMatchBanner } from '@/components/banner';
import { TableSkeleton } from '@/components/skeleton-loader';
import { Tooltip } from '@/components/tooltip';
import { useAccountTimezone } from '@/lib/accounts/context';
import { Json } from '@/lib/types';
import { isGroupedKey } from '@/explore/grouping';
import { sortGroupedFirst } from '@/explore/utils/field-ordering';

import { DataTableRow, DataTableProperty } from './types';
import { CellValue, getCellTitle } from './cell';
import { isNumberType } from '../../utils';
import { measureText } from '../../utils/text-measurement';
import { SortButton, isSortEnabledForColumn } from './sort-button';
import { Sort, SortItem } from '../../types';
import { useCellContextMenu, useColumnContextMenu } from './context-menu';
import { SortableItemHorizontal } from '../sortable-item';
import { OpenCellContextMenuParams } from './context-menu/use-cell-context-menu';
import { OpenColumnContextMenuParams } from './context-menu/use-column-context-menu';

export * from './types';

import tableStyle from '@/components/table/table.module.scss';

const MinDefaultColumnWidth = 100;
const MinColumnWidth = 50;
const MaxDefaultColumnWidth = 350;
const ColumnPadding = parseInt(tableStyle.columnHorizontalPadding, 10);

interface DataTableProps {
  records: DataTableRow[];
  properties: DataTableProperty[];
  grouping: string[];
  sort: Sort;
  setSort: (sort: SortItem | null) => void;
  reorderField: (fromIndex: number, toIndex: number, after: boolean) => void;
  setRef: (ref: HTMLElement | null) => void;
  excludedProperties?: string[];
  setExcludedProperties?: (properties: string[]) => void;
  canEditPipeline?: boolean;
  loading?: boolean;
}

const columnHelper = createColumnHelper<DataTableRow>();

const buildRowCells = (
  row: Row<DataTableRow>,
  timezone = '',
  sliceStart: number,
): React.ReactElement<any> => (
  <>
    {row
      .getVisibleCells()
      .slice(sliceStart, row.subRows.length > 0 ? sliceStart + 1 : undefined)
      .map((cell) => (
        <td
          key={cell.id}
          rowSpan={(row.original.$leaves as number) ?? 1}
          title={getCellTitle(cell, timezone)}
          className={classNames({
            [tableStyle.groupColumn]: row.original.$children !== undefined,
            [tableStyle.alignRight]: isNumberType(cell.column.columnDef.meta?.property.type),
          })}>
          <div className={tableStyle.content}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        </td>
      ))}
    {unlessNil(first(row.subRows), (subRow) => buildRowCells(subRow, timezone, sliceStart + 1))}
  </>
);

const buildRow = (
  row: Row<DataTableRow>,
  timezone = '',
  rowIdx = 0,
  depth = 0,
): React.ReactElement<any> => (
  <Fragment key={row.id}>
    {(depth === 0 || rowIdx > 0) && (
      <tr className={classNames({ [tableStyle.groupRow]: row.subRows.length > 0 })}>
        {buildRowCells(row, timezone, depth)}
      </tr>
    )}
    {row.subRows.map((subRow, idx) => buildRow(subRow, timezone, idx, depth + 1))}
  </Fragment>
);

const getRecordFromRow = (row: Row<DataTableRow>): Record<string, Json> => {
  const rows = [row, ...row.getParentRows()];
  return rows
    .flatMap((row) =>
      Object.keys(row.original)
        .filter((key) => !isGroupedKey(key))
        .map((key) => ({ [key]: row.original[key] })),
    )
    .reduce((acc, val) => ({ ...acc, ...val }), {});
};

/**
 * Outer component only for grabbing memoized context values without assuming
 * the entire context values are memoized. Avoids unnecessary re-renders of the
 * memoized inner component.
 */
export const DataTable = (props: DataTableProps) => {
  const openCellContextMenu = useCellContextMenu();
  const openColumnContextMenu = useColumnContextMenu();
  const timezone = useAccountTimezone();

  return (
    <DataTableInner
      {...props}
      openCellContextMenu={openCellContextMenu}
      openColumnContextMenu={openColumnContextMenu}
      timezone={timezone}
    />
  );
};

export const DataTableInner = memo(function DataTableInner(
  props: DataTableProps & {
    openCellContextMenu: (params: OpenCellContextMenuParams) => void;
    openColumnContextMenu: (params: OpenColumnContextMenuParams) => void;
    timezone?: string;
  },
) {
  const {
    properties,
    excludedProperties = [],
    setExcludedProperties,
    grouping,
    records,
    sort,
    setSort,
    setRef,
    canEditPipeline = true,
    loading = false,
    openCellContextMenu,
    openColumnContextMenu,
    timezone = '',
  } = props;

  const [tableElement, setTableElement] = useState<HTMLElement | null>(null);
  const [headerElement, setHeaderElement] = useState<HTMLElement | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const visibleProperties = useMemo(
    () => properties.filter(({ key }) => !excludedProperties.includes(key)),
    [excludedProperties, properties],
  );
  const sortedVisibleProperties = useMemo(
    () => sortGroupedFirst(visibleProperties, grouping),
    [visibleProperties, grouping],
  );

  const tableRef = useCallback(
    (ref: HTMLTableElement | null) => {
      setTableElement(ref);
      setRef(ref);
    },
    [setRef],
  );

  useStickyElement(headerElement, tableElement, tableStyle.floatingHeader);

  const defaultNumberOfColumns = 7;

  const getTextWidth = useCallback((text: string) => {
    const { columnHeaderFontSize, columnHeaderFontWeight, columnHeaderLetterSpacing, fontFamily } =
      tableStyle;
    const font = `${columnHeaderFontWeight} ${columnHeaderFontSize} ${fontFamily}`;
    // Column headers are uppercased via CSS
    return measureText(text.toLocaleUpperCase(), font, columnHeaderLetterSpacing);
  }, []);

  const getColumnDefaultSize = useCallback(
    (property: DataTableProperty) => {
      const computedTextWidth = getTextWidth(property.name);
      const desiredWidth = Math.ceil(computedTextWidth) + ColumnPadding * 2;
      const val = Math.min(Math.max(desiredWidth, MinDefaultColumnWidth), MaxDefaultColumnWidth);
      return val;
    },
    [getTextWidth],
  );

  const handleCellClick = useCallback(
    (
      event: React.MouseEvent,
      row: Row<DataTableRow>,
      property: DataTableProperty,
      value: unknown,
    ) => {
      event.stopPropagation();
      openCellContextMenu({
        position: {
          x: event.clientX - 10,
          y: event.clientY + 10,
        },
        row: getRecordFromRow(row),
        property,
        value,
        canEditPipeline,
        groupFilters: sortedVisibleProperties
          .filter((property) => property.pk ?? false)
          .map((property) => ({
            key: property.key,
            value: row.original[property.key],
          })),
      });
    },
    [openCellContextMenu, canEditPipeline, sortedVisibleProperties],
  );

  const columns = useMemo(() => {
    const columnWidths = sortedVisibleProperties.reduce<Record<string, number>>((acc, property) => {
      acc[property.key] = getColumnDefaultSize(property);
      return acc;
    }, {});

    return sortedVisibleProperties.map((property) =>
      columnHelper.accessor(property.key, {
        size: columnWidths[property.key],
        minSize: MinColumnWidth,
        header: property.name,
        meta: { property, propertyIndex: properties.findIndex((p) => p.key === property.key) },
        cell: (props) => (
          <CellValue
            value={props.row.original[property.key]}
            property={property}
            row={props.row}
            onClick={handleCellClick}
          />
        ),
        enableResizing: true,
      }),
    );
  }, [sortedVisibleProperties, getColumnDefaultSize, properties, handleCellClick]);

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (sortUpdater) => {
      const oldSort = sort.map(({ key, direction }) => ({ id: key, desc: direction === 'DESC' }));
      const newSort = typeof sortUpdater === 'function' ? sortUpdater(oldSort) : sortUpdater;
      if (newSort.length === 0) {
        setSort(null);
      } else {
        setSort(
          newSort.map(({ id, desc }): Sort[0] => ({
            key: id,
            direction: desc ? 'DESC' : 'ASC',
          }))[0],
        );
      }
    },
    [sort, setSort],
  );

  const tableOptions = useMemo<TableOptions<DataTableRow>>(
    () => ({
      columns,
      data: records,
      getCoreRowModel: getCoreRowModel(),
      columnResizeMode: 'onChange',
      getSubRows: (row) => (row.$children as DataTableRow[]) ?? [],
      state: {
        sorting: sort.map(({ key, direction }) => ({ id: key, desc: direction === 'DESC' })),
      },
      sortDescFirst: true,
      onSortingChange: handleSortingChange,
    }),
    [columns, records, sort, handleSortingChange],
  );

  const table = useReactTable(tableOptions);

  const handleHideColumn = useCallback(
    (key: string) => {
      if (setExcludedProperties === undefined) {
        return;
      }
      setExcludedProperties([...excludedProperties, key]);
    },
    [excludedProperties, setExcludedProperties],
  );

  const handleHeaderClick = (event: React.MouseEvent, property: DataTableProperty | undefined) => {
    if (property === undefined) {
      return;
    }

    openColumnContextMenu({
      position: { x: event.clientX - 10, y: event.clientY + 10 },
      property,
      canEditPipeline,
      hideColumn: handleHideColumn,
      isGroupedBy: grouping.includes(property.key),
    });
  };

  if (!loading && records.length === 0) {
    return <NoMatchBanner />;
  }

  const headerGroups = table.getHeaderGroups();
  const headersLength = headerGroups.at(0)?.headers.length ?? 0;

  const rows = loading ? (
    <TableSkeleton
      rows={10}
      cols={headersLength > 0 ? headersLength : defaultNumberOfColumns}
      type={'table'}
    />
  ) : (
    <tbody className="ph-no-capture">
      {table.getRowModel().rows.map((row) => buildRow(row, timezone))}
    </tbody>
  );

  const loadingClass = loading ? tableStyle.loading : '';

  return (
    <table
      className={classNames(tableStyle.table, tableStyle.dataTable, loadingClass)}
      ref={tableRef}>
      {rows}
      {/* thead after tbody fixes Safari z-index bug */}
      <thead
        ref={setHeaderElement}
        className={classNames({
          [tableStyle.resizing]: table.getState().columnSizingInfo.isResizingColumn !== false,
        })}>
        {headerGroups.map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const isDraggable =
                !grouping.includes(header.column.id) &&
                headerGroup.headers.length > 1 &&
                !header.column.getIsResizing();
              return (
                <th
                  colSpan={header.colSpan}
                  style={{ width: header.getSize() }}
                  key={header.id}
                  className={classNames({
                    [tableStyle.alignRight]: isNumberType(
                      header.column.columnDef.meta?.property.type,
                    ),
                    [tableStyle.resizing]: header.column.getIsResizing(),
                    [tableStyle.sorted]: header.column.getIsSorted() !== false,
                  })}>
                  <SortableItemHorizontal
                    scope={'property'}
                    index={header.column.columnDef.meta?.propertyIndex ?? 0}
                    draggedIndex={draggedIndex}
                    setDraggedIndex={setDraggedIndex}
                    onReorder={props.reorderField}
                    enabled={isDraggable}>
                    <div className={tableStyle.content}>
                      <div className={tableStyle.defaultLabel} onClick={(e) => e.stopPropagation()}>
                        <Tooltip
                          enabled={draggedIndex === null}
                          delay={150}
                          content={
                            <dl>
                              <dt>{header.column.columnDef.meta?.property.name}</dt>
                              <dd>{header.column.columnDef.meta?.property.description}</dd>
                            </dl>
                          }>
                          <button
                            className={tableStyle.label}
                            disabled={!canEditPipeline}
                            onClick={(event) => {
                              handleHeaderClick(event, header.column.columnDef.meta?.property);
                            }}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </button>
                        </Tooltip>
                        {header.column.getIsSorted() !== false && (
                          <SortButton
                            sortDirection={header.column.getIsSorted()}
                            onClick={header.column.getToggleSortingHandler()}
                          />
                        )}
                      </div>
                      <div
                        className={tableStyle.hoverLabel}
                        onClick={(e) => e.stopPropagation()}
                        aria-hidden>
                        <Tooltip
                          enabled={draggedIndex === null}
                          delay={150}
                          content={
                            <dl>
                              <dt>{header.column.columnDef.meta?.property.name}</dt>
                              <dd>{header.column.columnDef.meta?.property.description}</dd>
                            </dl>
                          }>
                          <button
                            className={tableStyle.label}
                            disabled={!canEditPipeline}
                            onClick={(event) => {
                              handleHeaderClick(event, header.column.columnDef.meta?.property);
                            }}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </button>
                        </Tooltip>
                        {isSortEnabledForColumn({
                          type: header.column.columnDef.meta?.property.type ?? '',
                        }) && (
                          <SortButton
                            sortDirection={header.column.getIsSorted()}
                            onClick={header.column.getToggleSortingHandler()}
                          />
                        )}
                      </div>
                      {header.column.getCanResize() && draggedIndex === null && (
                        <div
                          className={tableStyle.resizer}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onClick={(event) => event.stopPropagation()}
                          onDoubleClick={() => header.column.resetSize()}
                        />
                      )}
                    </div>
                  </SortableItemHorizontal>
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
    </table>
  );
});
